import Decimal from 'decimal.js'
import type { Coupon, Product } from '@entities/product/api/product.contract'
import { primaryOfferIdOf } from '@entities/product/model/single-offer'
import { AppError } from '@shared/api/error'
import type { Currency } from '@shared/contracts/enums'
import { currencyFractionDigits, formatMoney } from '@shared/lib/format/currency'
import { Money } from '@shared/lib/money/money'
import type { MonetaryValue } from '@shared/lib/money/money'
import { MINIMUM_PRODUCT_PRICE_AMOUNT } from './product-minimum-price'

export const COUPON_END_DATE_PAST_MESSAGE = 'A data final deve ser hoje ou posterior.'

export function couponAmountForCurrency(
  coupon: Pick<Coupon, 'amount'>,
  currency: Currency,
): Coupon['amount'] {
  return { amount: coupon.amount.amount, currency }
}

export function couponEndDateError(
  coupon: Pick<Coupon, 'active' | 'endsAt'>,
  today: string,
): string | undefined {
  if (!coupon.active) return undefined

  const endDate = coupon.endsAt.slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(endDate) || endDate === '9999-12-31' || endDate >= today) {
    return undefined
  }

  return COUPON_END_DATE_PAST_MESSAGE
}

const API_MINIMUM_PRICE_PATTERN =
  /^Cupom (\S+) invalido: cupom deve manter preco final >= (\d+(?:\.\d+)?) ([A-Z]{3})$/

type CouponPricedItem = { label: string; price: MonetaryValue }

type CouponPricedProduct = Pick<
  Product,
  'price' | 'offers' | 'links' | 'priceModel' | 'recurrencePlans'
>

// Mirrors the Core rule: an active coupon must keep every visible offer, active
// plan and paid trial at or above the commercial minimum.
function couponPricedItems(product: CouponPricedProduct): CouponPricedItem[] {
  const primaryOfferId = primaryOfferIdOf(product)
  const offers = product.offers.filter((offer) => offer.status !== 'INACTIVE')
  // The primary offer is renamed after the product on save, so it is not named here.
  const items: CouponPricedItem[] =
    offers.length > 0
      ? offers.map((offer) => ({
          label: offer.id === primaryOfferId ? 'a oferta principal' : `a oferta "${offer.name}"`,
          price: offer.price,
        }))
      : [{ label: 'a oferta principal', price: product.price }]
  if (product.priceModel !== 'subscription') return items

  for (const plan of product.recurrencePlans) {
    if (!plan.active) continue
    items.push({ label: `o plano "${plan.name}"`, price: plan.value })
    if (plan.trialEnabled && plan.trialType === 'PAID') {
      items.push({ label: `a entrada paga do plano "${plan.name}"`, price: plan.trialPrice })
    }
  }
  return items
}

function discountedPrice(coupon: Coupon, price: MonetaryValue): Money {
  const base = Money.fromMonetaryValue(price)
  const discount =
    coupon.type === 'percent'
      ? base.percent(Math.min(95, Math.max(1, coupon.percent)))
      : Money.fromMonetaryValue(couponAmountForCurrency(coupon, price.currency))
  const final = base.subtract(discount)
  return final.isNegative() ? new Money(0, price.currency) : final
}

type CouponMinimumPriceBreach = { item: CouponPricedItem; final: Money; minimum: Money }

function couponMinimumPriceBreach(
  coupon: Coupon,
  product: CouponPricedProduct,
): CouponMinimumPriceBreach | undefined {
  if (!coupon.active) return undefined

  for (const item of couponPricedItems(product)) {
    const { currency } = item.price
    // A fixed discount is stored in the product currency; Core skips other currencies.
    if (coupon.type === 'fixed' && currency !== product.price.currency) continue
    const final = discountedPrice(coupon, item.price)
    const minimum = new Money(MINIMUM_PRODUCT_PRICE_AMOUNT, currency)
    if (final.subtract(minimum).isNegative()) return { item, final, minimum }
  }
  return undefined
}

function formatFinalPrice(final: Money): string {
  // Round down so a final price just below the minimum never displays as the minimum.
  const amount = new Decimal(final.toAmountString())
    .toDecimalPlaces(currencyFractionDigits(final.currency), Decimal.ROUND_DOWN)
    .toString()
  return formatMoney(amount, final.currency)
}

export function couponMinimumPriceError(
  coupon: Coupon,
  product: CouponPricedProduct,
): string | undefined {
  const breach = couponMinimumPriceBreach(coupon, product)
  if (!breach) return undefined

  const { item, final, minimum } = breach
  return `Com este cupom, ${item.label} fica por ${formatFinalPrice(final)}. O preço final precisa ser de pelo menos ${formatMoney(minimum.toAmountString(), minimum.currency)}.`
}

// Plan prices are synced only after the product PATCH, so Core checks the coupons
// against the plans already saved. Call it after couponMinimumPriceError passes.
export function couponSavedPlanPriceError(
  coupon: Coupon,
  product: CouponPricedProduct,
  savedPlans: Product['recurrencePlans'],
): string | undefined {
  const breach = couponMinimumPriceBreach(coupon, { ...product, recurrencePlans: savedPlans })
  if (!breach) return undefined

  const { item, final, minimum } = breach
  return `Com este cupom, ${item.label} fica por ${formatFinalPrice(final)} no preço já salvo de ${formatMoney(item.price.amount, item.price.currency)}, abaixo do mínimo de ${formatMoney(minimum.toAmountString(), minimum.currency)}. Desative o cupom, salve as mudanças do plano e depois ative o cupom.`
}

export type CouponMinimumPriceApiFeedback = {
  couponId: string
  description: string
}

export function couponMinimumPriceApiFeedback(
  error: unknown,
): CouponMinimumPriceApiFeedback | undefined {
  if (!(error instanceof AppError) || error.code !== 'InvalidCouponConfigError') return undefined
  const match = API_MINIMUM_PRICE_PATTERN.exec(error.message)
  if (!match) return undefined
  const [, couponId = '', amount = '', currency = ''] = match

  return {
    couponId,
    description: `Um cupom ativo deixa uma oferta ou plano já salvo abaixo do preço mínimo de ${formatMoney(amount, currency)}. Reduza o desconto ou salve antes as mudanças de preço dos planos.`,
  }
}
